import { useState, useEffect, useCallback } from "react";
import { exchangeApi } from "@/lib/api";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Mail,
  Loader2,
  CheckCircle,
  XCircle,
  Eye,
  EyeOff,
  Calendar,
  Users,
  Link,
  Unlink,
  RefreshCw,
} from "lucide-react";

export function ExchangeSettings({ toast }) {
  const [exchangeConnected, setExchangeConnected] = useState(false);
  const [exchangeConnecting, setExchangeConnecting] = useState(false);
  const [exchangeSyncing, setExchangeSyncing] = useState(false);
  const [showExchangeSecret, setShowExchangeSecret] = useState(false);
  const [exchangeStatus, setExchangeStatus] = useState(null);
  const [exchangeForm, setExchangeForm] = useState({
    email: "",
    client_id: "",
    client_secret: "",
    tenant_id: "",
    server: "outlook.office365.com"
  });

  // Shared Calendars state
  const [sharedCalendars, setSharedCalendars] = useState([]);
  const [newSharedEmail, setNewSharedEmail] = useState("");
  const [addingSharedCalendar, setAddingSharedCalendar] = useState(false);
  const [removingCalendar, setRemovingCalendar] = useState(null);

  const fetchExchangeStatus = useCallback(async () => {
    try {
      const response = await exchangeApi.getStatus();
      setExchangeStatus(response.data);
      setExchangeConnected(response.data.connected);
      if (response.data.email) {
        setExchangeForm(prev => ({ ...prev, email: response.data.email }));
      }
    } catch (error) {
      console.error("Failed to get Exchange status", error);
    }
  }, []);

  const fetchSharedCalendars = useCallback(async () => {
    try {
      const response = await exchangeApi.listSharedCalendars();
      setSharedCalendars(response.data.shared_calendars || []);
    } catch (error) {
      console.error("Failed to get shared calendars", error);
    }
  }, []);

  useEffect(() => {
    fetchExchangeStatus();
    fetchSharedCalendars();
  }, [fetchExchangeStatus, fetchSharedCalendars]);

  const handleExchangeConnect = async () => {
    if (!exchangeForm.email || !exchangeForm.client_id || !exchangeForm.client_secret || !exchangeForm.tenant_id) {
      toast.error("Bitte alle Azure AD Felder ausfüllen");
      return;
    }
    
    setExchangeConnecting(true);
    try {
      const response = await exchangeApi.connect(
        exchangeForm.email,
        exchangeForm.client_id,
        exchangeForm.client_secret,
        exchangeForm.tenant_id,
        exchangeForm.server
      );
      
      if (response.data.success) {
        toast.success("Exchange OAuth2-Verbindung hergestellt");
        setExchangeConnected(true);
        fetchExchangeStatus();
      } else {
        toast.error(response.data.message || "Verbindung fehlgeschlagen");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Exchange-Verbindung fehlgeschlagen");
    } finally {
      setExchangeConnecting(false);
    }
  };

  const handleExchangeDisconnect = async () => {
    try {
      await exchangeApi.disconnect();
      toast.success("Exchange-Verbindung getrennt");
      setExchangeConnected(false);
      setExchangeStatus(null);
    } catch (error) {
      toast.error("Fehler beim Trennen");
    }
  };

  const handleSyncContacts = async () => {
    setExchangeSyncing(true);
    try {
      const response = await exchangeApi.syncAllCustomers();
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Synchronisation fehlgeschlagen");
    } finally {
      setExchangeSyncing(false);
    }
  };

  const handleAddSharedCalendar = async () => {
    if (!newSharedEmail.trim()) return;
    
    setAddingSharedCalendar(true);
    try {
      await exchangeApi.addSharedCalendar(newSharedEmail.trim());
      toast.success(`Kalender von ${newSharedEmail} hinzugefügt`);
      setNewSharedEmail("");
      fetchSharedCalendars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Hinzufügen des Kalenders");
    } finally {
      setAddingSharedCalendar(false);
    }
  };

  const handleRemoveSharedCalendar = async (email) => {
    setRemovingCalendar(email);
    try {
      await exchangeApi.removeSharedCalendar(email);
      toast.success(`Kalender von ${email} entfernt`);
      fetchSharedCalendars();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Entfernen des Kalenders");
    } finally {
      setRemovingCalendar(null);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-blue-500" />
              </div>
              <div>
                <CardTitle>Microsoft 365 Exchange</CardTitle>
                <CardDescription>
                  E-Mails, Kalender und Kontakte synchronisieren
                </CardDescription>
              </div>
            </div>
            <Badge variant={exchangeConnected ? "default" : "secondary"} className="gap-1">
              {exchangeConnected ? (
                <>
                  <CheckCircle className="w-3 h-3" />
                  Verbunden
                </>
              ) : (
                <>
                  <XCircle className="w-3 h-3" />
                  Nicht verbunden
                </>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-6">
          {exchangeConnected ? (
            <>
              {/* Connected State */}
              <div className="bg-muted/50 rounded-lg p-4 space-y-3">
                <div className="flex items-center gap-2 text-sm">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span className="font-medium">Verbunden als:</span>
                  <span className="text-muted-foreground">{exchangeStatus?.email}</span>
                </div>
                {exchangeStatus?.connected_at && (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Calendar className="w-4 h-4" />
                    Verbunden seit: {new Date(exchangeStatus.connected_at).toLocaleString('de-DE')}
                  </div>
                )}
              </div>

              <Separator />

              {/* Features */}
              <div>
                <h3 className="text-sm font-medium mb-4">Verfügbare Funktionen</h3>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <Mail className="w-5 h-5 text-blue-500" />
                    <div>
                      <p className="font-medium text-sm">E-Mails</p>
                      <p className="text-xs text-muted-foreground">Versand über Exchange</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <Calendar className="w-5 h-5 text-green-500" />
                    <div>
                      <p className="font-medium text-sm">Kalender</p>
                      <p className="text-xs text-muted-foreground">Termine synchronisieren</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 p-3 bg-muted/30 rounded-lg">
                    <Users className="w-5 h-5 text-purple-500" />
                    <div>
                      <p className="font-medium text-sm">Kontakte</p>
                      <p className="text-xs text-muted-foreground">Kunden synchronisieren</p>
                    </div>
                  </div>
                </div>
              </div>

              <Separator />

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleSyncContacts} 
                  disabled={exchangeSyncing}
                  variant="outline"
                >
                  {exchangeSyncing ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Alle Kunden synchronisieren
                </Button>
                <Button 
                  onClick={handleExchangeDisconnect} 
                  variant="destructive"
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  Verbindung trennen
                </Button>
              </div>
            </>
          ) : (
            <>
              {/* Connect Form - OAuth2 */}
              <div className="grid gap-4">
                <div className="p-3 bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    <strong>Azure AD OAuth2 Authentifizierung</strong><br />
                    Erstellen Sie eine App-Registrierung im Azure Portal unter Microsoft Entra ID → App-Registrierungen
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="exchange_email">E-Mail-Adresse *</Label>
                  <Input
                    id="exchange_email"
                    type="email"
                    value={exchangeForm.email}
                    onChange={(e) => setExchangeForm({ ...exchangeForm, email: e.target.value })}
                    placeholder="ihre.email@firma.de"
                    data-testid="exchange-email-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="exchange_tenant_id">Tenant-ID (Verzeichnis-ID) *</Label>
                  <Input
                    id="exchange_tenant_id"
                    value={exchangeForm.tenant_id}
                    onChange={(e) => setExchangeForm({ ...exchangeForm, tenant_id: e.target.value })}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    data-testid="exchange-tenant-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="exchange_client_id">Client-ID (Anwendungs-ID) *</Label>
                  <Input
                    id="exchange_client_id"
                    value={exchangeForm.client_id}
                    onChange={(e) => setExchangeForm({ ...exchangeForm, client_id: e.target.value })}
                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                    data-testid="exchange-client-id-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="exchange_client_secret">Client-Secret *</Label>
                  <div className="relative">
                    <Input
                      id="exchange_client_secret"
                      type={showExchangeSecret ? "text" : "password"}
                      value={exchangeForm.client_secret}
                      onChange={(e) => setExchangeForm({ ...exchangeForm, client_secret: e.target.value })}
                      placeholder="Client-Secret eingeben"
                      className="pr-10"
                      data-testid="exchange-client-secret-input"
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="absolute right-0 top-0 h-full"
                      onClick={() => setShowExchangeSecret(!showExchangeSecret)}
                    >
                      {showExchangeSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-muted-foreground">
                    Azure Portal → App → Zertifikate & Geheimnisse → Neuer geheimer Clientschlüssel
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="exchange_server">Server</Label>
                  <Input
                    id="exchange_server"
                    value={exchangeForm.server}
                    onChange={(e) => setExchangeForm({ ...exchangeForm, server: e.target.value })}
                    placeholder="outlook.office365.com"
                    data-testid="exchange-server-input"
                  />
                  <p className="text-xs text-muted-foreground">
                    Standard für Microsoft 365: outlook.office365.com
                  </p>
                </div>
              </div>

              <Button 
                onClick={handleExchangeConnect} 
                disabled={exchangeConnecting}
                className="w-full sm:w-auto"
                data-testid="exchange-connect-btn"
              >
                {exchangeConnecting ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Link className="w-4 h-4 mr-2" />
                )}
                Mit Exchange verbinden (OAuth2)
              </Button>
            </>
          )}
        </CardContent>
      </Card>

      {/* Shared Calendars Section - Only visible when connected */}
      {exchangeConnected && (
        <Card>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-500/10 flex items-center justify-center">
                <Users className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <CardTitle>Freigegebene Kalender</CardTitle>
                <CardDescription>
                  Kalender von Kollegen hinzufügen, um deren Termine zu sehen
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Add new shared calendar */}
            <div className="flex gap-2">
              <Input
                type="email"
                placeholder="kollege@firma.de"
                value={newSharedEmail}
                onChange={(e) => setNewSharedEmail(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && handleAddSharedCalendar()}
                data-testid="shared-calendar-email-input"
              />
              <Button 
                onClick={handleAddSharedCalendar}
                disabled={addingSharedCalendar || !newSharedEmail.trim()}
                data-testid="add-shared-calendar-btn"
              >
                {addingSharedCalendar ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Link className="w-4 h-4 mr-2" />
                )}
                Hinzufügen
              </Button>
            </div>

            <p className="text-xs text-muted-foreground">
              Der Kalender muss in Outlook/Microsoft 365 für Sie freigegeben sein.
            </p>

            {/* List of shared calendars */}
            {sharedCalendars.length > 0 ? (
              <div className="space-y-2">
                <Label>Verbundene Kalender ({sharedCalendars.length})</Label>
                <div className="border rounded-lg divide-y">
                  {sharedCalendars.map((email) => (
                    <div key={email} className="flex items-center justify-between p-3">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 text-indigo-500" />
                        <span className="text-sm">{email}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveSharedCalendar(email)}
                        disabled={removingCalendar === email}
                        className="text-destructive hover:text-destructive"
                        data-testid={`remove-calendar-${email}`}
                      >
                        {removingCalendar === email ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Unlink className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground border rounded-lg border-dashed">
                <Users className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">Keine freigegebenen Kalender</p>
                <p className="text-xs">Fügen Sie E-Mail-Adressen von Kollegen hinzu</p>
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
